// Survey 2.0 colour tokens. The mock mixes the brand into white with CSS
// color-mix(); that isn't available in the older iOS/Android webviews the
// native SDKs embed, so every mix is precomputed here and handed to the
// renderer as plain hex CSS variables.

type RGB = [number, number, number];

const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));

export const parseColor = (input?: string): RGB | null => {
  if (!input || typeof input !== "string") return null;
  const value = input.trim();
  const hex = value.replace(/^#/, "");
  if (/^[0-9a-f]{3}$/i.test(hex)) {
    return [0, 1, 2].map((i) => parseInt(hex[i] + hex[i], 16)) as RGB;
  }
  if (/^[0-9a-f]{6}([0-9a-f]{2})?$/i.test(hex)) {
    return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16)) as RGB;
  }
  const rgb = value.match(/^rgba?\(([^)]+)\)$/i);
  if (rgb) {
    const parts = rgb[1].split(/[\s,/]+/).map((p) => parseFloat(p));
    if (parts.length >= 3 && parts.slice(0, 3).every((p) => !isNaN(p))) {
      return [clamp(parts[0]), clamp(parts[1]), clamp(parts[2])];
    }
  }
  return null;
};

const toHex = ([r, g, b]: RGB) =>
  "#" + [r, g, b].map((c) => clamp(c).toString(16).padStart(2, "0")).join("");

/** `color-mix(in srgb, a pct%, b)` */
export const mix = (a: string, pct: number, b: string): string => {
  const ca = parseColor(a) ?? [0, 0, 0];
  const cb = parseColor(b) ?? [255, 255, 255];
  const t = pct / 100;
  return toHex([
    ca[0] * t + cb[0] * (1 - t),
    ca[1] * t + cb[1] * (1 - t),
    ca[2] * t + cb[2] * (1 - t),
  ]);
};

export const alpha = (color: string, a: number): string => {
  const c = parseColor(color) ?? [0, 0, 0];
  return `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${a})`;
};

const channel = (c: number) => {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
};

export const luminance = (color: string): number => {
  const c = parseColor(color) ?? [0, 0, 0];
  return 0.2126 * channel(c[0]) + 0.7152 * channel(c[1]) + 0.0722 * channel(c[2]);
};

export const contrastRatio = (a: string, b: string): number => {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

/**
 * Darkens (light surface) or lightens (dark surface) `color` in steps until it
 * reaches `minRatio` against `surface`. Used for brand-coloured TEXT (question
 * number, labels, outlined chips) so a pale brand (yellow, mint) stays legible.
 */
export const ensureContrast = (
  color: string,
  surface: string,
  minRatio: number,
): string => {
  let current = color;
  const towards = luminance(surface) > 0.4 ? "#000000" : "#ffffff";
  for (let step = 0; step < 12; step++) {
    if (contrastRatio(current, surface) >= minRatio) return current;
    current = mix(towards, 10 * (step + 1), color);
  }
  return current;
};

export type SurveyCorners = "sharp" | "rounded" | "pill";
export type SurveyBackground = "tint" | "plain" | "color" | "image";

/** The survey's design (definition.theme, filled by the server with widget defaults). */
export interface SurveyThemeInput {
  brandColor?: string;
  buttonTextColor?: string;
  appearance?: "auto" | "light" | "dark";
  background?: SurveyBackground;
  backgroundColor?: string;
  backgroundImage?: string;
  backgroundOverlay?: number;
  textColor?: string;
  corners?: SurveyCorners;
  logoUrl?: string;
  showLogo?: boolean;
  /** In-app full screen shows the logo only when this is on (the survey page always can). */
  logoInApp?: boolean;
  /** What "auto" means for the project's widget (server-resolved): light, dark or the visitor's system. */
  autoAppearance?: "light" | "dark" | "system";
}

export interface SurveyPalette {
  dark: boolean;
  /** Full screen / page backdrop. */
  background: SurveyBackground;
  /** Light text on the backdrop (dark colour / image with overlay). */
  backdropDark: boolean;
  corners: SurveyCorners;
  /** CSS `background` for the full-screen backdrop (colour / image + overlay). */
  backdrop?: string;
  logoUrl?: string;
  vars: Record<string, string>;
}

export const DEFAULT_DARK_SURFACE = "#18181b";
export const DEFAULT_IMAGE_OVERLAY = 0.3;
const INK = "#141820";
const DARK_INK = "#eef0f4";

/** Same rules as the server: https only, nothing that breaks out of url("…"). */
export const safeImageUrl = (value?: string): string | undefined => {
  if (!value || typeof value !== "string" || value.length > 2048) return undefined;
  // eslint-disable-next-line no-control-regex
  if (/[\s"'()<>\\`\u0000-\u001f]/.test(value)) return undefined;
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password
      ? value
      : undefined;
  } catch (exp) {
    return undefined;
  }
};

const clampOverlay = (value: unknown, fallback: number) =>
  typeof value === "number" && isFinite(value)
    ? Math.max(0, Math.min(0.8, value))
    : fallback;

/**
 * Radius scale for the whole survey: choices, scale cells, buttons and inputs,
 * plus the close button, the ending check badge, media and the step pills.
 * The card / sheet outline itself is drawn by the host (JS SDK, appnew.html),
 * which hears the corners through `survey-theme`.
 */
const RADII: Record<SurveyCorners, Record<string, string>> = {
  sharp: {
    "--sv-r-ctl": "3px",
    "--sv-r-btn": "3px",
    "--sv-r-cell": "3px",
    "--sv-r-cell-sm": "3px",
    "--sv-r-emoji": "3px",
    "--sv-r-key": "2px",
    "--sv-r-input": "3px",
    "--sv-r-area": "3px",
    "--sv-r-drop": "3px",
    "--sv-r-nav": "3px",
    "--sv-r-close": "8px",
    "--sv-r-badge": "12px",
    "--sv-r-media": "4px",
    "--sv-r-media-lg": "6px",
    "--sv-r-step": "1px",
  },
  rounded: {
    "--sv-r-ctl": "10px",
    "--sv-r-btn": "10px",
    "--sv-r-cell": "8px",
    "--sv-r-cell-sm": "7px",
    "--sv-r-emoji": "10px",
    "--sv-r-key": "5px",
    "--sv-r-input": "10px",
    "--sv-r-area": "10px",
    "--sv-r-drop": "10px",
    "--sv-r-nav": "7px",
    "--sv-r-close": "50%",
    "--sv-r-badge": "50%",
    "--sv-r-media": "10px",
    "--sv-r-media-lg": "14px",
    "--sv-r-step": "5px",
  },
  pill: {
    "--sv-r-ctl": "999px",
    "--sv-r-btn": "999px",
    "--sv-r-cell": "999px",
    "--sv-r-cell-sm": "999px",
    "--sv-r-emoji": "999px",
    "--sv-r-key": "999px",
    "--sv-r-input": "999px",
    "--sv-r-area": "18px",
    "--sv-r-drop": "18px",
    "--sv-r-nav": "999px",
    "--sv-r-close": "50%",
    "--sv-r-badge": "50%",
    "--sv-r-media": "18px",
    "--sv-r-media-lg": "24px",
    "--sv-r-step": "5px",
  },
};

/**
 * All renderer tokens for a survey theme on the project's widget background.
 * Light projects get exactly the mock's palette (white card, #141820 ink); a
 * dark appearance flips the surfaces. The card / sheet is always a solid
 * surface; the full screen and the survey page sit on the backdrop (brand
 * tint, plain, a colour or an image with an overlay), whose text colour is
 * picked by contrast unless the survey sets one.
 */
export const buildSurveyPalette = (
  themeInput: SurveyThemeInput | undefined,
  widget: { color?: string; backgroundColor?: string },
  /**
   * The survey page resolves "auto" like its page chrome (HelpCenterApp):
   * the widget's light / dark setting, or the visitor's system. In-app
   * surveys follow the widget colours the SDK already resolved.
   */
  page?: { systemDark: boolean },
): SurveyPalette => {
  const theme = themeInput ?? {};
  const brandSource = theme.brandColor || widget.color;
  const brand = parseColor(brandSource) ? toHex(parseColor(brandSource)!) : "#0f7b6c";
  const widgetBg = parseColor(widget.backgroundColor)
    ? toHex(parseColor(widget.backgroundColor)!)
    : undefined;
  const widgetDark = widgetBg ? luminance(widgetBg) < 0.18 : false;
  const dark =
    theme.appearance === "dark"
      ? true
      : theme.appearance === "light"
      ? false
      : page && theme.autoAppearance
      ? theme.autoAppearance === "dark" ||
        (theme.autoAppearance === "system" && page.systemDark)
      : widgetDark;

  const bg = dark ? (widgetDark ? widgetBg! : DEFAULT_DARK_SURFACE) : "#ffffff";
  const ink = dark ? DARK_INK : INK;
  const muted = dark ? "#9ba3af" : "#6b7280";
  const line = dark ? mix("#ffffff", 14, bg) : "#e4e7eb";
  const control = dark ? mix("#ffffff", 5, bg) : "#ffffff";

  // Text on a filled brand surface (selected chips, primary button).
  const onBrand = parseColor(theme.buttonTextColor)
    ? toHex(parseColor(theme.buttonTextColor)!)
    : contrastRatio("#ffffff", brand) >= 2.6
    ? "#ffffff"
    : INK;
  // Brand used as text / outline on the surface.
  const brandInk = ensureContrast(brand, bg, 3.2);

  // ── Backdrop (full screen + page) ──
  const image = theme.background === "image" ? safeImageUrl(theme.backgroundImage) : undefined;
  const solid =
    theme.background === "color" && parseColor(theme.backgroundColor)
      ? toHex(parseColor(theme.backgroundColor)!)
      : undefined;
  const background: SurveyBackground = image
    ? "image"
    : solid
    ? "color"
    : theme.background === "plain"
    ? "plain"
    : "tint";
  const overlay =
    background === "image"
      ? clampOverlay(theme.backgroundOverlay, DEFAULT_IMAGE_OVERLAY)
      : background === "color"
      ? clampOverlay(theme.backgroundOverlay, 0)
      : 0;
  const customText = parseColor(theme.textColor) ? toHex(parseColor(theme.textColor)!) : undefined;

  let full: Record<string, string>;
  let backdropDark = dark;
  let backdrop: string | undefined;
  if (background === "tint" || background === "plain") {
    const fullBg = background === "tint" ? mix(brand, dark ? 10 : 6, bg) : bg;
    const fullInk = customText ?? ink;
    backdropDark = luminance(fullBg) < 0.18;
    full = {
      "--sv-full-bg": fullBg,
      "--sv-full-ink": fullInk,
      "--sv-full-muted": customText ? mix(customText, 72, fullBg) : muted,
      "--sv-full-brand-ink": ensureContrast(brand, fullBg, 3.4),
      "--sv-full-cell-ink": ensureContrast(brand, fullBg, 3.4),
      "--sv-full-line": mix(brand, dark ? 45 : 32, bg),
      "--sv-full-control": mix(brand, dark ? 8 : 3, bg),
      "--sv-full-opt": mix(brand, dark ? 9 : 4, bg),
      "--sv-full-opt-ink":
        customText ??
        (dark ? mix(brand, 40, "#ffffff") : ensureContrast(mix(brand, 80, "#000000"), fullBg, 6)),
      "--sv-full-key-line": mix(brand, dark ? 60 : 45, bg),
      "--sv-full-ta-line": mix(brand, dark ? 55 : 40, bg),
      "--sv-full-track": mix(brand, dark ? 25 : 15, bg),
      "--sv-full-hover": mix(brand, dark ? 16 : 9, bg),
      "--sv-full-selected": mix(brand, dark ? 24 : 12, bg),
    };
  } else {
    // Colour or image: what the text actually sits on. A colour is known
    // exactly (darkened by the overlay); an image is unknown, so its overlay
    // decides — from 25 % black up, light text reads on any photo.
    const approx =
      background === "color"
        ? mix("#000000", overlay * 100, solid!)
        : customText
        ? luminance(customText) > 0.4
          ? "#2b2e33"
          : "#f2f2f2"
        : overlay >= 0.25 || dark
        ? "#2b2e33"
        : "#f2f2f2";
    const fullInk =
      customText ?? (contrastRatio("#ffffff", approx) >= contrastRatio(INK, approx) ? "#ffffff" : INK);
    const light = luminance(fullInk) > 0.4;
    backdropDark = light;
    const glass = "#ffffff";
    full = {
      "--sv-full-bg": approx,
      "--sv-full-ink": fullInk,
      "--sv-full-muted": alpha(fullInk, light ? 0.78 : 0.68),
      "--sv-full-brand-ink": customText ?? ensureContrast(brand, approx, 3.4),
      // Scale numbers and choice keys read in the text colour on a photo / colour.
      "--sv-full-cell-ink": fullInk,
      "--sv-full-line": alpha(fullInk, light ? 0.38 : 0.22),
      "--sv-full-control": alpha(glass, light ? 0.1 : 0.6),
      "--sv-full-opt": alpha(glass, light ? 0.1 : 0.6),
      "--sv-full-opt-ink": fullInk,
      "--sv-full-key-line": alpha(fullInk, light ? 0.5 : 0.35),
      "--sv-full-ta-line": alpha(fullInk, light ? 0.55 : 0.4),
      "--sv-full-track": alpha(fullInk, light ? 0.25 : 0.14),
      "--sv-full-hover": alpha(glass, light ? 0.18 : 0.82),
      "--sv-full-selected": alpha(brand, light ? 0.34 : 0.16),
    };
    const shade = overlay > 0 ? `linear-gradient(${alpha("#000000", overlay)}, ${alpha("#000000", overlay)}), ` : "";
    backdrop =
      background === "image"
        ? `${shade}url(${JSON.stringify(image)}) center / cover no-repeat, ${approx}`
        : `${shade}${solid}`;
  }

  // Only drawn in in-app full screen, and only when the survey turns it on (default: survey page only).
  const logoUrl =
    theme.showLogo === false || theme.logoInApp !== true ? undefined : safeImageUrl(theme.logoUrl);
  const corners: SurveyCorners =
    theme.corners === "sharp" || theme.corners === "pill" ? theme.corners : "rounded";

  return {
    dark,
    background,
    backdropDark,
    corners,
    backdrop,
    logoUrl,
    vars: {
      "--sv-brand": brand,
      "--sv-on-brand": onBrand,
      "--sv-brand-ink": brandInk,
      "--sv-bg": bg,
      "--sv-ink": ink,
      "--sv-muted": muted,
      "--sv-surface-ink": ink,
      "--sv-surface-muted": muted,
      "--sv-line": line,
      "--sv-control": control,
      "--sv-hover": mix(brand, 7, control),
      "--sv-selected": mix(brand, dark ? 18 : 9, control),
      "--sv-brand-soft": mix(brand, dark ? 22 : 12, bg),
      "--sv-ring": alpha(brand, 0.18),
      "--sv-focus": alpha(brandInk, 0.55),
      "--sv-track": dark ? alpha("#ffffff", 0.1) : "rgba(16, 24, 40, 0.06)",
      "--sv-ghost-hover": dark ? alpha("#ffffff", 0.08) : "rgba(16, 24, 40, 0.05)",
      "--sv-error": dark ? "#ff8a80" : "#c9372c",
      "--sv-error-soft": dark ? alpha("#ff8a80", 0.14) : "#fdeceb",
      ...full,
      ...RADII[corners],
    },
  };
};
